import { memo, useCallback, useEffect, useState } from 'react';

import {
	Confirm,
	Modal,
	Select,
	SpinnerOverlay,
	SubmissionButtons,
} from '@components/common';

import useQueryConsistencyCheckRanGetAvailableTemplate from '@hooks/cmModule/consistencyCheckRan/useQueryConsistencyCheckRanGetAvailableTemplate';

import { useModal } from '@components/common/Modal/utils';

import { TConsistencyCheckAssignTemplateDTO } from '@interfaces/cmModule/dto/consistencyCheckDTO';
import { IId } from '@interfaces/common/common';
import { TSelectOption } from '@interfaces/components/componentTypes';

import { MODAL_WIDTH_480 } from '@constants/modals';
import { REASSIGN_TEXT, UNASSIGN_TEXT } from '../ConsistencyCheckActions/AssignConsistencyCheckTemplateMenu/BulkAssignConsistencyCheckTemplate/constants';
import { ASSIGN_SELECT_PLACEHOLDER } from '../constants';

interface IAssignConsistencyCheckTemplateForm {
	id?: string;
	assignedTemplateId?: string;
	assignTemplate: (data: TConsistencyCheckAssignTemplateDTO) => void;
	unassignTemplate: (data: IId<string>) => void;
	isLoading?: boolean;
	errorMessage?: string;
}

const AssignConsistencyCheckTemplateForm = ({
	id,
	assignedTemplateId,
	errorMessage,
	isLoading,
	assignTemplate,
	unassignTemplate,
}: IAssignConsistencyCheckTemplateForm) => {
	const { onClose } = useModal();

	const [selectedOption, setSelectedOption] = useState<TSelectOption<string>>();
	const [isConfirm, setIsConfirm] = useState(false);
	const [isUnassign, setIsUnassign] = useState(false);

	const {
		availableTemplates,
		isLoadingAvailableTemplates,
	} = useQueryConsistencyCheckRanGetAvailableTemplate({ id });

	const onChange = useCallback((option: TSelectOption<string>) => {
		setSelectedOption(option);
	}, []);

	const openConfirm = useCallback(() => setIsConfirm(true), []);
	const closeConfirm = useCallback(() => setIsConfirm(false), []);

	const openUnassignConfirm = useCallback(() => {
		setIsUnassign(true);
		openConfirm();
	}, [openConfirm]);

	const handleAssignTemplate = useCallback(() => {
		if (!selectedOption?.id || !id) {
			return;
		}

		assignTemplate({
			consistencyId: id,
			goldenTemplateId: selectedOption.id.toString(),
		});
	}, [
		id,
		selectedOption,
		assignTemplate,
	]);

	const handleUnassignTemplate = useCallback(() => {
		if (!id) {
			return;
		}

		unassignTemplate({ id });
	}, [
		id,
		unassignTemplate,
	]);

	const onConfirmSubmit = useCallback(() => {
		if (!isUnassign) {
			handleAssignTemplate();
		} else {
			handleUnassignTemplate();
		}
	}, [
		isUnassign,
		handleAssignTemplate,
		handleUnassignTemplate,
	]);

	const positiveHandler = useCallback(() => {
		if (assignedTemplateId) {
			setIsUnassign(false);

			return openConfirm();
		}

		handleAssignTemplate();
	}, [
		assignedTemplateId,
		openConfirm,
		handleAssignTemplate,
	]);

	useEffect(() => {
		if (assignedTemplateId) {
			const selected = availableTemplates.find((template) => template && template.id === assignedTemplateId);

			setSelectedOption(selected);
		}
	}, [assignedTemplateId, availableTemplates]);

	const isPositiveDisabled = isLoading
		|| isLoadingAvailableTemplates
		|| assignedTemplateId === selectedOption?.id
		|| !selectedOption?.id;

	return (
		<div>
			{isLoadingAvailableTemplates || isLoading && <SpinnerOverlay />}
			<Modal
				isOpen={isConfirm}
				onClose={closeConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={closeConfirm}
					onSubmit={onConfirmSubmit}
					question={isUnassign
						? UNASSIGN_TEXT
						: REASSIGN_TEXT
					}
				>
					{!isUnassign && (<span>Current reference template will be substituted.</span>)}
				</Confirm>
			</Modal>
			<Select
				errorMessage={errorMessage}
				handleChange={onChange}
				isLoading={isLoadingAvailableTemplates}
				isWithoutUseForms
				label='Reference Template'
				name='assignTemplate'
				options={availableTemplates}
				placeholder={ASSIGN_SELECT_PLACEHOLDER}
				required
				value={selectedOption}
			/>
			<SubmissionButtons
				disabledPositive={isPositiveDisabled}
				isProceedButtonNeeded={!!assignedTemplateId}
				negativeHandler={onClose}
				positiveHandler={positiveHandler}
				positiveLabel='Save'
				proceedHandler={openUnassignConfirm}
				proceedLabel='Unassign'
			/>
		</div>
	);
};

export default memo(AssignConsistencyCheckTemplateForm);
